import React from 'react';

import { findOverflowScrollParent } from '@atlaskit/editor-common/Popup/utils';
import type { EditorAppearance } from '@atlaskit/editor-common/types/editor-appearance';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import {
	type NamedPluginStatesFromInjectionAPI,
	useSharedPluginStateWithSelector,
} from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { MediaFeatureFlags } from '@atlaskit/media-common/types';
import type { MediaClientConfig } from '@atlaskit/media-core/auth';
import { DropzoneLoader as Dropzone } from '@atlaskit/media-picker/dropzone';
import type { DropzoneConfig } from '@atlaskit/media-picker/types';

import type { MediaNextEditorPluginType } from '../../mediaPluginType';
import type PickerFacade from '../../pm-plugins/picker-facade';
import PickerFacadeProvider from './PickerFacadeProvider';

type Props = {
	api: ExtractInjectionAPI<MediaNextEditorPluginType> | undefined;
	appearance: EditorAppearance;
	editorDomElement: Element;
	featureFlags?: MediaFeatureFlags;
	isActive: boolean;
};

const selector = (
	states: NamedPluginStatesFromInjectionAPI<
		ExtractInjectionAPI<MediaNextEditorPluginType>,
		'media'
	>,
) => {
	return {
		options: states.mediaState?.options,
		handleDrag: states.mediaState?.handleDrag,
	};
};

type DropzoneWrapperInternalProps = Props & {
	config: DropzoneConfig;
	mediaClientConfig: MediaClientConfig;
	pickerFacadeInstance: PickerFacade;
};

const DropzoneWrapperInternal = ({
	api,
	isActive,
	featureFlags,
	editorDomElement,
	appearance,
	mediaClientConfig,
	config,
	pickerFacadeInstance,
}: DropzoneWrapperInternalProps) => {
	const { options, handleDrag } = useSharedPluginStateWithSelector(api, ['media'], selector);
	const { customDropzoneContainer } = options || {};

	// Ignored via go/ees005
	// eslint-disable-next-line @atlaskit/editor/no-as-casting
	const editorHtmlElement = editorDomElement as HTMLElement;
	const scrollParent = appearance === 'full-page' && findOverflowScrollParent(editorHtmlElement);
	const container = customDropzoneContainer || (scrollParent ? scrollParent : editorHtmlElement);
	const dropzoneConfig: DropzoneConfig = {
		...config,
		container,
	};

	return isActive ? (
		<Dropzone
			mediaClientConfig={mediaClientConfig}
			config={dropzoneConfig}
			onError={pickerFacadeInstance.handleUploadError}
			onPreviewUpdate={pickerFacadeInstance.handleUploadPreviewUpdate}
			onEnd={pickerFacadeInstance.handleReady}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onDragEnter={() => handleDrag?.('enter')}
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onDragLeave={() => handleDrag?.('leave')}
			featureFlags={featureFlags}
		/>
	) : null;
};

export const DropzoneWrapper = ({
	api,
	isActive,
	featureFlags,
	editorDomElement,
	appearance,
}: Props): React.JSX.Element => (
	<PickerFacadeProvider api={api} analyticsName="dropzone">
		{({ mediaClientConfig, config, pickerFacadeInstance }) => (
			<DropzoneWrapperInternal
				api={api}
				isActive={isActive}
				featureFlags={featureFlags}
				editorDomElement={editorDomElement}
				appearance={appearance}
				mediaClientConfig={mediaClientConfig}
				config={config}
				pickerFacadeInstance={pickerFacadeInstance}
			/>
		)}
	</PickerFacadeProvider>
);
